import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import SpinningLoader from "../components/SpinningLoader";
import {
  useCryptoAssets,
  useFetchAllTradablePairs,
} from "../hooks/query/queries";
import { usePortfolioEstimate } from "./PortfolioEstimateContext";
import { usePairStore } from "../store/useStore";
import { useAuth } from "./AuthContext";

const TradingContext = createContext();

export const TradingProvider = ({ children }) => {
  const { isAuthenticated, user: userProfile } = useAuth();
  const { setPairObject } = usePairStore();
  const [showLoaderWithDelay, setShowLoaderWithDelay] = useState(false);

  const { data: tradingAssets, isLoading: isLoadingAssets } = useCryptoAssets();
  const { data: pairObject, isLoading: isLoadingPairs } =
    useFetchAllTradablePairs();
  const { portfolioData: walletBalanceData, isLoading: isLoadingWallet } =
    usePortfolioEstimate();

  useEffect(() => {
    if (pairObject) {
      setPairObject(pairObject);
    }
  }, [pairObject, setPairObject]);

  const wallet = useMemo(() => {
    if (!walletBalanceData) return null;
    return {
      ...walletBalanceData,
      assetBalances: {
        crypto: (walletBalanceData?.assets || []).filter(
          (asset) => asset.currencyType === "Crypto" || asset.type === "Crypto"
        ),
        fiat: (walletBalanceData?.assets || []).filter(
          (asset) => asset.currencyType === "Fiat" || asset.type === "Fiat"
        ),
      },
    };
  }, [walletBalanceData]);

  const zarBalance = useMemo(() => {
    return wallet?.assetBalances?.fiat?.zar?.availableAmount ?? 0;
  }, [wallet]);

  const isLoading = isLoadingAssets || isLoadingPairs || isLoadingWallet;

  const hasData = tradingAssets || pairObject || walletBalanceData;

  useEffect(() => {
    if (hasData && !isLoading) {
      setShowLoaderWithDelay(true);
      setShowLoaderWithDelay(false);
    } else if (!hasData) {
      setShowLoaderWithDelay(false);
    }
  }, [hasData, isLoading]);

  const showLoader =
    isAuthenticated && ((isLoading && !hasData) || showLoaderWithDelay);

  const value = useMemo(
    () => ({
      tradingAssets,
      pairObject,
      walletBalance: walletBalanceData,
      wallet,
      zarBalance,
      isLoading,
      isLoadingAssets,
      isLoadingPairs,
      isLoadingWallet,
      userId: userProfile?._id,
      userProfile,
    }),
    [
      tradingAssets,
      pairObject,
      walletBalanceData,
      wallet,
      zarBalance,
      isLoading,
      isLoadingAssets,
      isLoadingPairs,
      isLoadingWallet,
      userProfile,
    ]
  );

  if (showLoader) {
    return <SpinningLoader />;
  }

  return (
    <TradingContext.Provider value={value}>{children}</TradingContext.Provider>
  );
};

export const useTrading = () => {
  const context = useContext(TradingContext);
  if (!context) {
    throw new Error("useTrading must be used within TradingProvider");
  }
  return context;
};
